import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { useShift } from '../../../context/ShiftContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { SHIFT_TYPES, SHIFT_STATUS, TEAM_ROLES } from '../../../data/nurseAppModels';
import SwapShiftModal from './SwapShiftModal';

const ShiftDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { clockIn, clockOut } = useShift();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSwapModal, setShowSwapModal] = useState(false);

  // Fetch shift data
  useEffect(() => {
    const fetchShift = async () => {
      try {
        setLoading(true);
        const shiftRef = doc(db, 'shifts', id);
        const shiftSnap = await getDoc(shiftRef);

        if (shiftSnap.exists()) {
          setShift({
            id: shiftSnap.id,
            ...shiftSnap.data()
          });
        } else {
          setError('ไม่พบเวร');
        }
      } catch (err) {
        console.error('Error fetching shift:', err);
        setError('เกิดข้อผิดพลาดในการโหลดข้อมูลเวร');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchShift();
    }
  }, [id]);

  // Get shift type label
  const getShiftTypeLabel = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return 'เช้า';
      case SHIFT_TYPES.AFTERNOON: return 'บ่าย';
      case SHIFT_TYPES.NIGHT: return 'ดึก';
      default: return type;
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case SHIFT_STATUS.SCHEDULED: return 'กำหนดแล้ว';
      case SHIFT_STATUS.CONFIRMED: return 'ยืนยันแล้ว';
      case SHIFT_STATUS.IN_PROGRESS: return 'กำลังทำงาน';
      case SHIFT_STATUS.COMPLETED: return 'เสร็จสิ้น';
      case SHIFT_STATUS.CANCELLED: return 'ยกเลิก';
      default: return status;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case SHIFT_STATUS.COMPLETED:
        return 'bg-green-100 text-green-700';
      case SHIFT_STATUS.IN_PROGRESS:
        return 'bg-blue-100 text-blue-700';
      case SHIFT_STATUS.CANCELLED:
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Handle clock in
  const handleClockIn = async () => {
    if (!window.confirm('ยืนยันการเข้าเวร?')) return;

    setActionLoading(true);
    try {
      const result = await clockIn(shift.id);
      if (result.success) {
        // Refresh shift data
        const shiftRef = doc(db, 'shifts', shift.id);
        const shiftSnap = await getDoc(shiftRef);
        if (shiftSnap.exists()) {
          setShift({
            id: shiftSnap.id,
            ...shiftSnap.data()
          });
        }
      } else {
        alert('เกิดข้อผิดพลาด: ' + result.error);
      }
    } catch (err) {
      alert('เกิดข้อผิดพลาด: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle clock out
  const handleClockOut = async () => {
    if (!window.confirm('ยืนยันการออกเวร?')) return;

    setActionLoading(true);
    try {
      const result = await clockOut(shift.id);
      if (result.success) {
        // Refresh shift data
        const shiftRef = doc(db, 'shifts', shift.id);
        const shiftSnap = await getDoc(shiftRef);
        if (shiftSnap.exists()) {
          setShift({
            id: shiftSnap.id,
            ...shiftSnap.data()
          });
        }
      } else {
        alert('เกิดข้อผิดพลาด: ' + result.error);
      }
    } catch (err) {
      alert('เกิดข้อผิดพลาด: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Check if current user can perform actions
  const isMyShift = shift?.assignedTo === currentUser?.uid;
  const canClockIn = isMyShift && shift?.status === SHIFT_STATUS.SCHEDULED || shift?.status === SHIFT_STATUS.CONFIRMED;
  const canClockOut = isMyShift && shift?.status === SHIFT_STATUS.IN_PROGRESS;
  const canManage = currentTeam && (
    currentTeam.userRole === TEAM_ROLES.LEADER || 
    currentTeam.userRole === TEAM_ROLES.DEPUTY_LEADER
  );

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format time
  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('th-TH', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate working hours
  const calculateWorkingHours = () => {
    if (!shift?.clockInTime || !shift?.clockOutTime) return null;
    
    const clockIn = shift.clockInTime.toDate ? shift.clockInTime.toDate() : new Date(shift.clockInTime);
    const clockOut = shift.clockOutTime.toDate ? shift.clockOutTime.toDate() : new Date(shift.clockOutTime);
    const diff = clockOut - clockIn;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours} ชั่วโมง ${minutes} นาที`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (error || !shift) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{error || 'ไม่พบเวร'}</p>
          <button
            onClick={() => navigate('/nurse/shifts')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            กลับไปหน้าจัดการเวร
          </button>
        </div>
      </div>
    );
  }

  const startTime = shift.startTime?.toDate ? shift.startTime.toDate() : new Date(shift.startTime);
  const endTime = shift.endTime?.toDate ? shift.endTime.toDate() : new Date(shift.endTime);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/shifts')}
            className="text-blue-600 hover:text-blue-700 mb-4 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            กลับไปหน้าจัดการเวร
          </button>
          <h1 className="text-2xl font-bold text-gray-800">รายละเอียดเวร</h1>
        </div>

        {/* Shift Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          {/* Shift Type & Status */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                เวร{getShiftTypeLabel(shift.shiftType)}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {formatDate(shift.startTime)}
              </p>
            </div>
            <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(shift.status)}`}>
              {getStatusLabel(shift.status)}
            </span>
          </div>

          {/* Time */}
          <div className="mb-4 pb-4 border-b border-gray-200">
            <div className="flex items-center text-gray-700 mb-2">
              <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">เวลา:</span>
              <span className="ml-2">
                {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
              </span>
            </div>
          </div>

          {/* Assigned To */}
          <div className="mb-4 pb-4 border-b border-gray-200">
            <div className="flex items-center text-gray-700 mb-2">
              <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="font-medium">ผู้รับผิดชอบ:</span>
              <span className="ml-2">{shift.assignedToName || 'ยังไม่กำหนด'}</span>
            </div>
          </div>

          {/* Department */}
          {shift.department && (
            <div className="mb-4 pb-4 border-b border-gray-200">
              <div className="flex items-center text-gray-700 mb-2">
                <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span className="font-medium">แผนก:</span>
                <span className="ml-2">{shift.department}</span>
              </div>
            </div>
          )}

          {/* Ward */}
          {shift.ward && (
            <div className="mb-4 pb-4 border-b border-gray-200">
              <div className="flex items-center text-gray-700 mb-2">
                <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="font-medium">หอผู้ป่วย:</span>
                <span className="ml-2">{shift.ward}</span>
              </div>
            </div>
          )}

          {/* Clock In/Out Times */}
          {(shift.clockInTime || shift.clockOutTime) && (
            <div className="mb-4 pb-4 border-b border-gray-200">
              <h3 className="font-medium text-gray-700 mb-2">บันทึกเวลา:</h3>
              {shift.clockInTime && (
                <div className="flex items-center text-sm text-gray-600 mb-1">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>เข้าเวร: {formatTime(shift.clockInTime)} ({formatDate(shift.clockInTime)})</span>
                </div>
              )}
              {shift.clockOutTime && (
                <div className="flex items-center text-sm text-gray-600">
                  <span className="text-red-600 mr-2">✓</span>
                  <span>ออกเวร: {formatTime(shift.clockOutTime)} ({formatDate(shift.clockOutTime)})</span>
                </div>
              )}
              {shift.clockInTime && shift.clockOutTime && (
                <div className="mt-2 text-sm font-medium text-blue-600">
                  ⏱️ ทำงาน: {calculateWorkingHours()}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          {shift.notes && (
            <div className="mb-4">
              <h3 className="font-medium text-gray-700 mb-2">หมายเหตุ:</h3>
              <p className="text-gray-600 text-sm">{shift.notes}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="font-medium text-gray-700 mb-4">การดำเนินการ</h3>
          <div className="flex flex-col gap-3">
            {canClockIn && (
              <button
                onClick={handleClockIn}
                disabled={actionLoading}
                className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? 'กำลังดำเนินการ...' : 'เข้าเวร'}
              </button>
            )}
            {canClockOut && (
              <button
                onClick={handleClockOut}
                disabled={actionLoading}
                className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? 'กำลังดำเนินการ...' : 'ออกเวร'}
              </button>
            )}
            {isMyShift && shift.status === SHIFT_STATUS.SCHEDULED && (
              <button
                onClick={() => setShowSwapModal(true)}
                className="w-full px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
              >
                ขอแลกเวร
              </button>
            )}
            {!canClockIn && !canClockOut && !isMyShift && (
              <p className="text-gray-500 text-sm text-center">ไม่มีคำสั่งที่สามารถดำเนินการได้</p>
            )}
          </div>
        </div>

        {/* Swap Shift Modal */}
        {showSwapModal && (
          <SwapShiftModal
            isOpen={showSwapModal}
            onClose={(success) => {
              setShowSwapModal(false);
              if (success) {
                // Refresh shift data
                const refreshShift = async () => {
                  const shiftRef = doc(db, 'shifts', shift.id);
                  const shiftSnap = await getDoc(shiftRef);
                  if (shiftSnap.exists()) {
                    setShift({
                      id: shiftSnap.id,
                      ...shiftSnap.data()
                    });
                  }
                };
                refreshShift();
              }
            }}
            shiftId={shift.id}
            shiftData={shift}
          />
        )}
      </div>
    </div>
  );
};

export default ShiftDetailPage;

